import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Upload } from "antd";
import { ImageIcon, X } from "lucide-react";
import { useDispatch } from "react-redux";
import { setLoading, showToast } from "../../store/slices/common";
import { updateKaraoke } from "../../http/karaoke";
import {
  isValidMediaId,
  MAX_STICKERS,
  normalizeStickers,
} from "../ManageChallenge/helpers";
import {
  createStickerUploadHandler,
  rememberUploadedSticker,
  syncStickerListRef,
} from "../../utils/stickerUploads";

const { Dragger } = Upload;

/**
 * Quick sticker editor for a karaoke track. Opened from the row actions menu
 * so stickers can be added/removed without walking through the full upload
 * flow (lyrics, sync, etc.).
 *
 * Pre-existing stickers only have URLs (no Media ids) — they're sent back as
 * keepStickerUrls, while newly uploaded ones are sent as stickerIds. The
 * backend merges the two, so partial edits don't require re-uploading.
 */
const StickersModal = ({ open, onClose, karaoke, onSuccess }) => {
  const [stickers, setStickers] = useState([]);
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch();
  const stickersRef = useRef([]);
  syncStickerListRef(stickersRef, stickers);
  const karaokeIdRef = useRef(karaoke?._id);
  karaokeIdRef.current = karaoke?._id;
  const stickerUploadRef = useRef(null);
  if (!stickerUploadRef.current) {
    stickerUploadRef.current = createStickerUploadHandler({
      max: MAX_STICKERS,
      getCurrent: () => stickersRef.current.length,
      folder: "karaoke-stickers",
      metadata: () => ({
        type: "sticker",
        ...(karaokeIdRef.current && { karaokeId: karaokeIdRef.current }),
      }),
      onUploaded: (media) => {
        setStickers(rememberUploadedSticker(stickersRef, media, MAX_STICKERS));
      },
      dispatch,
      setLoading: (value) => dispatch(setLoading(value)),
    });
  }

  useEffect(() => {
    if (karaoke) {
      // Saved stickers are bare URL strings; normalize to {mediaId, url}
      setStickers(normalizeStickers(karaoke));
    }
  }, [karaoke]);

  const handleStickerUpload = stickerUploadRef.current;

  const handleRemoveSticker = (index) => {
    setStickers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearStickers = () => {
    setStickers([]);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      dispatch(setLoading(true));

      // Existing stickers only have URLs (no Media ids). Keep those via
      // keepStickerUrls; newly uploaded ones go in stickerIds. Omit
      // keepStickerUrls when empty — prod Joi rejects unknown fields, and
      // empty keep is equivalent to a plain stickerIds replace.
      const keepStickerUrls = stickers
        .filter((sticker) => !isValidMediaId(sticker.mediaId))
        .map((sticker) => sticker.url);
      const stickerIds = stickers
        .filter((sticker) => isValidMediaId(sticker.mediaId))
        .map((sticker) => sticker.mediaId);

      const payload = { stickerIds };
      if (keepStickerUrls.length) {
        payload.keepStickerUrls = keepStickerUrls;
      }

      await updateKaraoke(payload, karaoke._id);
      dispatch(
        showToast({ color: "success", msg: "Stickers updated successfully." })
      );
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Error updating stickers:", err);
      dispatch(
        showToast({
          color: "error",
          msg:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to update stickers",
        })
      );
    } finally {
      setSaving(false);
      dispatch(setLoading(false));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} color="#142C5C">
            Edit Stickers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {karaoke?.title} - {karaoke?.artist}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.5 }}
        >
          Shown on the recording edit screen for this karaoke track. PNG keeps
          transparency. Up to {MAX_STICKERS} stickers.
        </Typography>

        {stickers.length > 0 && (
          <Box className="sticker-preview-row">
            {stickers.map((sticker, index) => (
              <Box
                key={sticker.mediaId || sticker.url}
                className="contest-upload-preview contest-upload-preview--sticker"
              >
                <img src={sticker.url} alt={`sticker ${index + 1}`} />
                <button
                  className="contest-upload-remove"
                  onClick={() => handleRemoveSticker(index)}
                  type="button"
                >
                  <X size={14} />
                </button>
              </Box>
            ))}
          </Box>
        )}

        {stickers.length < MAX_STICKERS && (
          <Box sx={{ mt: stickers.length ? 1.5 : 0 }}>
            <Dragger
              multiple
              showUploadList={false}
              customRequest={handleStickerUpload}
              accept="image/*"
              className="contest-upload-dragger"
            >
              <Box className="contest-upload-dragger-content">
                <ImageIcon size={32} strokeWidth={1.5} color="#667eea" />
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                  Drag & drop stickers here
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  or click to browse ({stickers.length} of {MAX_STICKERS} added,
                  PNG/JPG, max 5MB)
                </Typography>
              </Box>
            </Dragger>
          </Box>
        )}

        {stickers.length > 0 && (
          <Button
            size="small"
            onClick={handleClearStickers}
            sx={{
              mt: 1,
              textTransform: "none",
              color: "#d32f2f",
              padding: 0,
              minWidth: 0,
            }}
          >
            Clear all stickers
          </Button>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#142C5C",
            color: "#142C5C",
            borderRadius: "6px",
            "&:hover": {
              borderColor: "#0f2247",
              backgroundColor: "transparent",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            backgroundColor: "#142C5C",
            color: "#fff",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor: "#0f2247",
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StickersModal;
